import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";

// Native ERP port of the standalone Sales Reporter app's "Visits" tab.
// Mirrors the actual api/add_visit.php / api/update_visit.php / api/get_visits.php:
// same fields, same self-service scope (a rep only ever sees/edits their
// own visits), no delete (the original app never exposed one either).
//
// Notes on parity with the PHP: visit_type/outcome/customer_category fall
// back to a sensible default server-side if left unset, so the pickers
// below default to those same values rather than forcing a choice.
// visit_date/follow_up_date are plain YYYY-MM-DD strings, matching the
// <input type="date"> value format directly (no ISO-datetime conversion
// needed). There is no km/notes field on a visit in the original app —
// free text lives in "purpose", "remarks" and "competitor_notes" instead.
//
// Customer dropdown: pulls from the ERP's existing Customer Master via
// the new, read-only GET /sales-report/customers (added after Phase 6 —
// see sales_report.py). Picking an entry just autofills Contact Name/
// Phone from it — visits still have no customer_id field, so this is a
// pure convenience layer; free-typing a name for a walk-in/prospect not
// in the master list still works exactly as before.
//
// "+ Add Customer": the write side of the same sync, via POST
// /sales-report/customers — a rep who can't find a customer in the
// dropdown creates one on the spot (name/contact/phone/city only, no
// code/GSTIN) instead of falling back to free-typing. It's written into
// the ERP's own Customer Master, so it shows up on the Customers page
// immediately, and — the same route the standalone canaresonline.com
// Sales Reporter app's own "Visits" tab calls for its own "+ Add
// Customer" — in that app's dropdown too. Newly created customer is
// auto-selected right after, same as picking one from the list.
const VISIT_TYPES = [
  { value: "customer", label: "Customer" },
  { value: "dealer", label: "Dealer" },
  { value: "consultant", label: "Consultant" },
  { value: "office", label: "Office" },
];
const OUTCOMES = [
  { value: "pending", label: "Pending" },
  { value: "successful", label: "Successful" },
  { value: "follow_up", label: "Follow-up needed" },
  { value: "not_interested", label: "Not interested" },
];
const CUSTOMER_CATEGORIES = [
  { value: "direct", label: "Direct" },
  { value: "distributor", label: "Distributor" },
  { value: "retailer", label: "Retailer" },
  { value: "industrial", label: "Industrial" },
  { value: "other", label: "Other" },
];

const today = () => new Date().toISOString().slice(0, 10);

const empty = {
  visit_type: "customer",
  contact_name: "",
  contact_phone: "",
  visit_date: today(),
  customer_category: "direct",
  purpose: "",
  product_discussed: "",
  outcome: "pending",
  follow_up_date: "",
  quotation_sent: false,
  quotation_value: 0,
  order_received: false,
  order_value: 0,
  payment_collected: false,
  payment_value: 0,
  competitor_notes: "",
  remarks: "",
  latitude: "",
  longitude: "",
  gps_address: "",
};

export default function SalesVisitsPage() {
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: "", contact: "", phone: "", city: "" });
  const [savingCustomer, setSavingCustomer] = useState(false);

  const load = async () => setList((await api.get("/sales-report/visits")).data);
  const loadCustomers = async () => {
    try {
      setCustomers((await api.get("/sales-report/customers")).data);
    } catch (e) {
      // Don't let a failed customer-lookup fetch block the rest of the
      // page (free-typing a contact name still works either way) — but
      // surface it instead of leaving the dropdown silently/mysteriously
      // empty with no clue why.
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load the Customer Master list");
    }
  };
  useEffect(() => { load(); loadCustomers(); }, []);

  const startCreate = () => { setEditing(null); setForm(empty); setSelectedCustomerId(""); setOpen(true); };
  const startEdit = (v) => {
    setEditing(v.id);
    setForm({
      ...empty,
      ...v,
      follow_up_date: v.follow_up_date || "",
      latitude: v.latitude ?? "",
      longitude: v.longitude ?? "",
    });
    setSelectedCustomerId("");
    setOpen(true);
  };

  const pickCustomer = (customerId) => {
    setSelectedCustomerId(customerId);
    const c = customers.find((x) => x.id === customerId);
    if (!c) return;
    setForm((f) => ({ ...f, contact_name: c.name, contact_phone: c.phone || f.contact_phone }));
  };

  const startAddCustomer = () => {
    setNewCustomer({ name: "", contact: "", phone: "", city: "" });
    setAddCustomerOpen(true);
  };

  const saveNewCustomer = async () => {
    if (!newCustomer.name.trim()) return toast.error("Customer name is required");
    setSavingCustomer(true);
    try {
      const created = (await api.post("/sales-report/customers", newCustomer)).data;
      setCustomers((list) => [...list, created].sort((a, b) => a.name.localeCompare(b.name)));
      pickCustomer(created.id);
      toast.success(`${created.name} added to the Customer Master`);
      setAddCustomerOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't add the customer");
    } finally {
      setSavingCustomer(false);
    }
  };

  const save = async () => {
    if (form.visit_type !== "office" && !form.contact_name.trim()) {
      return toast.error("Please enter the customer/dealer/consultant name");
    }
    const payload = {
      ...form,
      quotation_value: Math.max(0, Number(form.quotation_value || 0)),
      order_value: Math.max(0, Number(form.order_value || 0)),
      payment_value: Math.max(0, Number(form.payment_value || 0)),
      follow_up_date: form.follow_up_date || null,
      latitude: form.latitude === "" ? null : Number(form.latitude),
      longitude: form.longitude === "" ? null : Number(form.longitude),
    };
    setBusy(true);
    try {
      if (editing) await api.put(`/sales-report/visits/${editing}`, payload);
      else await api.post("/sales-report/visits", payload);
      toast.success(editing ? "Visit updated" : "Visit logged");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="sales-visits-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Sales Report</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Visits
          </h1>
          <p className="text-sm text-slate-500 mt-1">Log customer, dealer, consultant & office visits — outcomes, quotations, orders.</p>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={startCreate} data-testid="new-visit-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New Visit
            </Button>
          </SheetTrigger>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            <SheetHeader><SheetTitle data-testid="visit-sheet-title">{editing ? "Edit Visit" : "New Visit"}</SheetTitle></SheetHeader>

            <div className="space-y-5 mt-6">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Visit Type</Label>
                  <Select value={form.visit_type} onValueChange={(v) => setForm({ ...form, visit_type: v })}>
                    <SelectTrigger data-testid="visit-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VISIT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Visit Date</Label>
                  <Input type="date" value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} data-testid="visit-date-input" />
                </div>
              </div>

              <div>
                <Label>Customer (optional — pick from Customer Master)</Label>
                <div className="flex gap-2">
                  <Select value={selectedCustomerId} onValueChange={pickCustomer}>
                    <SelectTrigger className="flex-1" data-testid="visit-customer-select"><SelectValue placeholder="Select an existing customer…" /></SelectTrigger>
                    <SelectContent>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.code} · {c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" onClick={startAddCustomer} data-testid="add-customer-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add Customer
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Fills in the contact name/phone below — not in the list? Add it, or just type a name in directly.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{form.visit_type === "office" ? "Contact Name (optional — defaults to \"Office Work\")" : "Contact Name *"}</Label>
                  <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="visit-contact-input" />
                </div>
                <div>
                  <Label>Contact Phone</Label>
                  <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="visit-phone-input" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Customer Category</Label>
                  <Select value={form.customer_category} onValueChange={(v) => setForm({ ...form, customer_category: v })}>
                    <SelectTrigger data-testid="visit-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CUSTOMER_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Outcome</Label>
                  <Select value={form.outcome} onValueChange={(v) => setForm({ ...form, outcome: v })}>
                    <SelectTrigger data-testid="visit-outcome-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {OUTCOMES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Purpose</Label>
                <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="visit-purpose-input" />
              </div>
              <div>
                <Label>Product Discussed</Label>
                <Input value={form.product_discussed} onChange={(e) => setForm({ ...form, product_discussed: e.target.value })} data-testid="visit-product-input" />
              </div>

              <div>
                <Label>Follow-up Date</Label>
                <Input type="date" value={form.follow_up_date} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })} data-testid="visit-followup-input" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <Checkbox checked={form.quotation_sent} onCheckedChange={(v) => setForm({ ...form, quotation_sent: !!v })} data-testid="visit-quotation-sent" />
                  Quotation sent
                </label>
                <div>
                  <Label>Quotation Value (₹)</Label>
                  <Input type="number" min="0" value={form.quotation_value} onChange={(e) => setForm({ ...form, quotation_value: e.target.value })} data-testid="visit-quotation-value" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <Checkbox checked={form.order_received} onCheckedChange={(v) => setForm({ ...form, order_received: !!v })} data-testid="visit-order-received" />
                  Order received
                </label>
                <div>
                  <Label>Order Value (₹)</Label>
                  <Input type="number" min="0" value={form.order_value} onChange={(e) => setForm({ ...form, order_value: e.target.value })} data-testid="visit-order-value" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <Checkbox checked={form.payment_collected} onCheckedChange={(v) => setForm({ ...form, payment_collected: !!v })} data-testid="visit-payment-collected" />
                  Payment collected
                </label>
                <div>
                  <Label>Payment Value (₹)</Label>
                  <Input type="number" min="0" value={form.payment_value} onChange={(e) => setForm({ ...form, payment_value: e.target.value })} data-testid="visit-payment-value" />
                </div>
              </div>

              <div>
                <Label>Competitor Notes</Label>
                <Textarea rows={2} value={form.competitor_notes} onChange={(e) => setForm({ ...form, competitor_notes: e.target.value })} data-testid="visit-competitor-notes" />
              </div>
              <div>
                <Label>Remarks</Label>
                <Textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="visit-remarks-input" />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.12em] text-slate-500">Location (optional)</Label>
                <div className="grid grid-cols-2 gap-3 mt-1">
                  <Input type="number" step="any" placeholder="Latitude" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} data-testid="visit-lat-input" />
                  <Input type="number" step="any" placeholder="Longitude" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} data-testid="visit-lng-input" />
                </div>
                <Input className="mt-2" placeholder="GPS address" value={form.gps_address} onChange={(e) => setForm({ ...form, gps_address: e.target.value })} data-testid="visit-gps-address-input" />
              </div>
            </div>

            <SheetFooter className="mt-6 gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} disabled={busy} data-testid="save-visit-btn">Save</Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="visits-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
              <th className="py-3 px-4">Date</th>
              <th className="py-3 px-4">Type</th>
              <th className="py-3 px-4">Contact</th>
              <th className="py-3 px-4">Outcome</th>
              <th className="py-3 px-4">Quotation</th>
              <th className="py-3 px-4">Order</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((v) => (
              <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`visit-row-${v.id}`}>
                <td className="py-3 px-4 text-slate-600">{v.visit_date}</td>
                <td className="py-3 px-4 capitalize text-slate-800">{v.visit_type}</td>
                <td className="py-3 px-4 text-slate-800">
                  {v.contact_name}
                  {v.contact_phone ? <span className="text-xs text-slate-500"> · {v.contact_phone}</span> : null}
                </td>
                <td className="py-3 px-4 text-xs text-slate-600 capitalize">{(v.outcome || "").replace("_", " ")}</td>
                <td className="py-3 px-4 mono text-xs text-slate-600">
                  {v.quotation_sent ? `₹ ${Number(v.quotation_value || 0).toLocaleString()}` : "—"}
                </td>
                <td className="py-3 px-4 mono text-xs text-slate-600">
                  {v.order_received ? `₹ ${Number(v.order_value || 0).toLocaleString()}` : "—"}
                </td>
                <td className="py-3 px-4 text-right">
                  <Button variant="ghost" size="icon" onClick={() => startEdit(v)} data-testid={`edit-visit-${v.id}`}><Pencil className="w-4 h-4 text-slate-500" /></Button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={7} className="py-10 text-center text-slate-500 text-sm">No visits logged yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={addCustomerOpen} onOpenChange={setAddCustomerOpen}>
        <DialogContent data-testid="add-customer-dialog">
          <DialogHeader><DialogTitle>Add Customer</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Customer / Dealer Name *</Label>
              <Input
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                placeholder="e.g. Sharma Traders"
                data-testid="new-customer-name-input"
              />
            </div>
            <div>
              <Label>Contact Person</Label>
              <Input
                value={newCustomer.contact}
                onChange={(e) => setNewCustomer({ ...newCustomer, contact: e.target.value })}
                data-testid="new-customer-contact-input"
              />
            </div>
            <div>
              <Label>Contact Phone</Label>
              <Input
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                placeholder="98765 43210"
                data-testid="new-customer-phone-input"
              />
            </div>
            <div>
              <Label>City</Label>
              <Input
                value={newCustomer.city}
                onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
                placeholder="e.g. Andheri East, Mumbai"
                data-testid="new-customer-city-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCustomerOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={saveNewCustomer} disabled={savingCustomer} data-testid="save-new-customer-btn">
              Add Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
